// Video Studio icin gorsel kaynagi secici.
//
// Kaynaklar: dosya yukleme, Listing Builder urunleri, Etsy urunleri ve (taki
// magazasinda) Studio'da uretilen urun fotograflari.
// Mockup'lar bilerek disarida: video icin urunun kendi gorseli daha isabetli.
// Uzak gorseller
// dogrudan ComfyUI'ye verilemez (tarayici blob'a cevirmek zorunda ve
// etsystatic CORS basligi vermiyor), o yuzden picker sadece URL'i secer;
// blob'a cevirme isini sayfa API proxy'si uzerinden yapar.

import { useEffect, useState } from "react";
import { Upload, Loader2, Image as ImageIcon, Store, RefreshCw, Sparkles, ArrowLeft } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useShopVertical } from "../../lib/useShopVertical";
import { useShotProducts, type ShotProduct } from "../../lib/useShotProducts";

export type PickedSource =
  | { kind: "upload"; file: File; label: string }
  | { kind: "remote"; url: string; label: string; /** Urunun taslaklari (video eklerken one cikar) */ draftIds?: string[] };

interface DraftItem {
  id: string;
  title: string;
  sourceImageUrl?: string | null;
  imageUrls?: string[];
  productTypeKey?: string | null;
  designVariations?: Array<{ imageUrl?: string | null }>;
}

/** Taslakta gorsel birden fazla yerde olabilir; ilk bulunani kullan. */
function draftImage(d: DraftItem): string | null {
  return d.imageUrls?.[0] || d.sourceImageUrl || d.designVariations?.[0]?.imageUrl || null;
}

interface EtsyItem {
  listing_id: number;
  title: string;
  images?: Array<{ url_570xN: string; url_fullxfull: string }>;
}

const abs = (u?: string | null) =>
  !u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`;

type Tab = "upload" | "draft" | "etsy" | "shots";

export function ProductSourcePicker({
  onPick,
  selectedLabel,
}: {
  onPick: (s: PickedSource) => void;
  selectedLabel?: string | null;
}) {
  // Takida varsayilan kaynak Studio'nun urettigi urun fotograflari
  const { isConceptVertical } = useShopVertical();
  const [tab, setTab] = useState<Tab>(isConceptVertical ? "shots" : "upload");
  const { data: shotData, isLoading: shotsLoading, refetch: refetchShots } = useShotProducts(tab === "shots");
  const [shotProduct, setShotProduct] = useState<ShotProduct | null>(null);
  const [shots, setShots] = useState<Array<{ id: string; imageUrl: string; thumbnailUrl?: string | null }>>([]);
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [etsy, setEtsy] = useState<EtsyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (which: Tab) => {
    if (which === "upload") return;
    if (which === "shots") { void refetchShots(); return; }
    setLoading(true);
    setError(null);
    try {
      if (which === "draft") {
        const res = await fetch(`${getBackendUrl()}/api/listing-builder`, { headers: authHeaders() });
        if (!res.ok) throw new Error("Ürün listesi alınamadı");
        setDrafts((await res.json()).data ?? []);
      } else {
        const qs = new URLSearchParams({ state: "active", offset: "0", limit: "40" });
        const res = await fetch(`${getBackendUrl()}/api/etsy/listings?${qs}`, { headers: authHeaders() });
        if (!res.ok) throw new Error("Etsy ürünleri alınamadı");
        const body = await res.json();
        setEtsy(body.data?.results ?? []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "shots") void load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Urun secilince o urunun uretilmis fotograflari
  useEffect(() => {
    if (!shotProduct) { setShots([]); return; }
    setLoading(true); setError(null);
    fetch(`${getBackendUrl()}/api/product-shots?catalogId=${shotProduct.catalogId}&categoryId=${shotProduct.categoryId}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("Fotoğraflar alınamadı"))))
      .then((b) => setShots(b.data ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [shotProduct]);

  const tabBtn = (id: Tab, label: string, Icon: React.ElementType) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition ${
        tab === id ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "hover:bg-gray-50"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {isConceptVertical && tabBtn("shots", "Ürün fotoğrafları", Sparkles)}
        {tabBtn("upload", "Yükle", Upload)}
        {tabBtn("draft", "Ürünlerim", ImageIcon)}
        {tabBtn("etsy", "Etsy ürünlerim", Store)}
        {tab !== "upload" && (
          <button
            onClick={() => void load(tab)}
            className="ml-auto p-1.5 rounded-lg border hover:bg-gray-50"
            title="Yenile"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {tab === "upload" && (
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f?.type.startsWith("image/")) onPick({ kind: "upload", file: f, label: f.name });
          }}
          className="border-2 border-dashed rounded-lg h-40 flex items-center justify-center cursor-pointer hover:border-indigo-400"
        >
          <div className="text-center text-gray-400">
            <Upload className="w-6 h-6 mx-auto mb-1" />
            <p className="text-sm">Görseli sürükle ya da tıkla</p>
            {selectedLabel && <p className="text-xs text-gray-500 mt-1">Secili: {selectedLabel}</p>}
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPick({ kind: "upload", file: f, label: f.name });
            }}
          />
        </label>
      )}

      {tab !== "upload" && (
        <div className="border rounded-lg h-40 overflow-y-auto p-2">
          {(loading || (tab === "shots" && shotsLoading)) && (
            <div className="h-full flex items-center justify-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {error && <p className="text-sm text-red-600 p-2">{error}</p>}

          {/* Studio'da uretilen urun fotograflari: once urun, sonra fotograf */}
          {!loading && !shotsLoading && !error && tab === "shots" && (shotProduct ? (
            <div>
              <button onClick={() => setShotProduct(null)} className="mb-1.5 flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                <ArrowLeft className="h-3 w-3" /> {shotProduct.name}
              </button>
              <div className="grid grid-cols-5 gap-2">
                {shots.map((s) => (
                  <button key={s.id}
                    onClick={() => onPick({ kind: "remote", url: abs(s.imageUrl), label: shotProduct.name.split(" / ")[0], draftIds: shotProduct.drafts.map((d) => d.id) })}
                    className="aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-indigo-500">
                    <img src={abs(s.thumbnailUrl || s.imageUrl)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          ) : (shotData?.items.length ? (
            <div className="grid grid-cols-5 gap-2">
              {shotData.items.map((p) => (
                <button key={p.key} onClick={() => setShotProduct(p)} title={`${p.name} · ${p.shotCount} fotoğraf`}
                  className="relative aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-indigo-500">
                  <img src={abs(p.coverUrl)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute bottom-0.5 right-0.5 rounded bg-black/60 px-1 text-[10px] text-white">{p.shotCount}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 p-2">Studio'da fotoğrafı üretilmiş ürün yok.</p>
          )))}

          {!loading && !error && tab === "draft" && (() => {
            const withImage = drafts.filter((d) => draftImage(d));
            return withImage.length ? (
              <div className="grid grid-cols-5 gap-2">
                {withImage.map((d) => (
                  <button
                    key={d.id}
                    onClick={() =>
                      onPick({ kind: "remote", url: abs(draftImage(d)!), label: d.title })
                    }
                    className="aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-indigo-500"
                    title={d.title}
                  >
                    <img
                      src={abs(draftImage(d)!)}
                      alt={d.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 p-2">
                Listing Builder'da görselli ürün bulunamadı.
              </p>
            );
          })()}

          {!loading && !error && tab === "etsy" && (
            etsy.length ? (
              <div className="grid grid-cols-5 gap-2">
                {etsy
                  .filter((l) => l.images?.length)
                  .map((l) => (
                    <button
                      key={l.listing_id}
                      onClick={() =>
                        onPick({
                          kind: "remote",
                          url: l.images![0].url_fullxfull,
                          label: l.title.slice(0, 60),
                        })
                      }
                      className="aspect-square rounded border overflow-hidden hover:ring-2 hover:ring-indigo-500"
                      title={l.title}
                    >
                      <img
                        src={l.images![0].url_570xN}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 p-2">
                Aktif Etsy ürünü bulunamadı. Mağaza bağlantısını kontrol et.
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}
